import { Badge } from 'components/ui/badge';
import { Checkbox } from 'components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover';
import { cn } from 'lib/utils';
import { ChevronDownIcon, XIcon } from 'lucide-react';
import * as React from 'react';

export type MultiSelectOption = { value: string; label: string };

export type MultiSelectProps = {
  options: MultiSelectOption[];
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  'aria-label'?: string;
  'aria-invalid'?: boolean;
};

/**
 * A `Select` that takes several values: the field shows the chosen options
 * as removable tags, and opens a list of checkable options (what Ant
 * Design's `Select mode="multiple"` was). The list is a plain listbox: the
 * options are few and known, so there is no search.
 */
function MultiSelect({
  options,
  value,
  onChange,
  placeholder,
  disabled,
  className,
  'aria-label': ariaLabel,
  'aria-invalid': ariaInvalid,
}: MultiSelectProps) {
  const [open, setOpen] = React.useState(false);
  const selected = options.filter(option => value.includes(option.value));

  const toggle = (optionValue: string) =>
    onChange(value.includes(optionValue) ? value.filter(v => v !== optionValue) : [...value, optionValue]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          aria-label={ariaLabel}
          aria-invalid={ariaInvalid}
          disabled={disabled}
          data-slot="multi-select-trigger"
          data-placeholder={selected.length === 0 ? '' : undefined}
          className={cn(
            // Same box as SelectTrigger, grown to fit the tags
            "border-input data-[placeholder]:text-subtle-foreground focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-ring-destructive/20 aria-invalid:border-ring-destructive flex min-h-11 w-full cursor-pointer items-center gap-2 rounded-lg border bg-card px-3 py-1.5 text-left text-sm shadow-xs transition-[color,box-shadow] outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 [&_svg:not([class*='text-'])]:text-muted-foreground",
            className,
          )}
        >
          <span className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
            {selected.length === 0
              ? placeholder
              : selected.map(option => (
                  <Badge key={option.value} variant="secondary" data-slot="multi-select-tag" className="max-w-full">
                    <span className="truncate">{option.label}</span>
                    {/* Not a button: it sits inside the trigger button */}
                    <span
                      role="button"
                      tabIndex={disabled ? -1 : 0}
                      aria-label={`${option.label} ×`}
                      className="-mr-1 inline-flex size-4 shrink-0 cursor-pointer items-center justify-center rounded-full hover:bg-foreground/10"
                      onClick={event => {
                        event.stopPropagation();
                        toggle(option.value);
                      }}
                      onKeyDown={event => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          event.stopPropagation();
                          toggle(option.value);
                        }
                      }}
                    >
                      <XIcon className="size-3" />
                    </span>
                  </Badge>
                ))}
          </span>
          <ChevronDownIcon className="size-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-(--radix-popover-trigger-width) p-1">
        <ul role="listbox" aria-multiselectable aria-label={ariaLabel} className="m-0 max-h-64 overflow-y-auto p-0">
          {options.map(option => {
            const checked = value.includes(option.value);
            return (
              <li
                key={option.value}
                role="option"
                aria-selected={checked}
                tabIndex={0}
                data-slot="multi-select-option"
                // 44px rows: a comfortable touch target
                className="flex min-h-11 cursor-pointer items-center gap-2.5 rounded-md px-2 py-2.5 text-sm outline-none select-none hover:bg-accent focus-visible:bg-accent"
                onClick={() => toggle(option.value)}
                onKeyDown={event => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    toggle(option.value);
                  }
                }}
              >
                <Checkbox checked={checked} tabIndex={-1} aria-hidden className="pointer-events-none" />
                <span className="leading-tight">{option.label}</span>
              </li>
            );
          })}
        </ul>
      </PopoverContent>
    </Popover>
  );
}

export { MultiSelect };
